import * as actions from './actions';
import * as resourceFavoriteActions from '../resource-favorite/actions';

import { ActionType, createReducer } from 'typesafe-actions';
import { ACTION_STATUSES } from 'shared/consts';

import { Resource } from 'store/resource/types';
import { ResourcesState } from './types';

export const initialState: ResourcesState = {
  resources: null,
  infiniteResources: {},
  hasMoreInfiniteResources: true,
  getInfiniteResources: {
    status: null,
    error: null,
  },
  total: null,
  loading: false,
  error: null,
  favoritesOnly: false,
};

const getResourcesRequestHandler = (
  state: ResourcesState,
  { payload }: ActionType<typeof actions.getResources.request>,
) => ({
  ...state,
  loading: true,
  error: null as SerializableError,
  favoritesOnly: Boolean(payload.filterParams?.favorites_only),
});

const getResourcesSuccessHandler = (
  state: ResourcesState,
  { payload }: ActionType<typeof actions.getResources.success>,
): ResourcesState => ({
  ...state,
  resources: payload.resources,
  total: payload.total,
  loading: false,
  error: null,
});

const getResourcesFailureHandler = (
  state: ResourcesState,
  { payload: error }: ActionType<typeof actions.getResources.failure>,
): ResourcesState => ({
  ...state,
  loading: false,
  error: error.error,
});

const resetResourceHandler = (state: ResourcesState): ResourcesState => ({
  ...state,
  loading: false,
  resources: null,
  error: null,
});

const getInfiniteResourcesRequestHandler = (
  state: ResourcesState,
  { payload }: ActionType<typeof actions.getInfiniteResources.request>,
): ResourcesState => ({
  ...state,
  infiniteResources: { ...state.infiniteResources, [payload.indexOfSet]: null },
  getInfiniteResources: {
    status: ACTION_STATUSES.PENDING,
    error: null,
  },
  favoritesOnly: Boolean(payload.filterParams?.favorites_only),
});

const getInfiniteResourcesSuccessHandler = (
  state: ResourcesState,
  { payload }: ActionType<typeof actions.getInfiniteResources.success>,
): ResourcesState => ({
  ...state,
  infiniteResources: { ...state.infiniteResources, [payload.indexOfSet]: payload.resources },
  hasMoreInfiniteResources: Boolean(payload.paging.next),
  total: payload.total,
  getInfiniteResources: {
    status: ACTION_STATUSES.FULFILLED,
    error: null,
  },
});

const getInfiniteResourcesFailureHandler = (
  state: ResourcesState,
  { payload: error }: ActionType<typeof actions.getInfiniteResources.failure>,
): ResourcesState => ({
  ...state,
  getInfiniteResources: {
    status: ACTION_STATUSES.REJECTED,
    error: error.error,
  },
});

const resetInfiniteResourcesStateHandler = (state: ResourcesState): ResourcesState => ({
  ...state,
  infiniteResources: {},
  hasMoreInfiniteResources: true,
  getInfiniteResources: {
    status: null,
    error: null,
  },
  total: null,
});

const patchResourceFavorite = (resource: Resource, resourceUuid: string, isFavorite: boolean): Resource =>
  resource.uuid === resourceUuid ? { ...resource, is_favorite: isFavorite } : resource;

const applyResourceFavorite = (state: ResourcesState, resourceUuid: string, isFavorite: boolean): ResourcesState => ({
  ...state,
  resources: state.resources?.map(resource => patchResourceFavorite(resource, resourceUuid, isFavorite)),
  infiniteResources: Object.fromEntries(
    Object.entries(state.infiniteResources).map(([indexOfSet, resources]) => [
      indexOfSet,
      resources?.map(resource => patchResourceFavorite(resource, resourceUuid, isFavorite)),
    ]),
  ),
});

const toggleResourceFavoriteRequestHandler = (
  state: ResourcesState,
  { payload }: ActionType<typeof resourceFavoriteActions.toggleResourceFavorite.request>,
): ResourcesState => applyResourceFavorite(state, payload.resourceUuid, payload.nextIsFavorite);

const toggleResourceFavoriteFailureHandler = (
  state: ResourcesState,
  { payload }: ActionType<typeof resourceFavoriteActions.toggleResourceFavorite.failure>,
): ResourcesState => applyResourceFavorite(state, payload.resourceUuid, payload.isFavorite);

const removeResource = (resources: Array<Resource>, resourceUuid: string): Array<Resource> =>
  resources?.filter(resource => resource.uuid !== resourceUuid);

/*
 * A confirmed un-favorite on a favorites-only list (`favorites_only=true`) no longer belongs there —
 * patching its flag would leave an unfavorited resource sitting in a "favorites only" view until the
 * next refetch. Removal waits for `.success` rather than happening optimistically on `.request`: the
 * `.request` handler above already flips the flag immediately for instant star feedback, and if the
 * call then fails, `.failure` reverts that flag on a resource that's still in the list — removing
 * eagerly would instead require re-inserting a resource this reducer no longer has a handle on.
 * Favoriting, and any toggle on a plain (non-favorites-only) list, keeps the existing patch-only
 * behavior.
 */
const toggleResourceFavoriteSuccessHandler = (
  state: ResourcesState,
  { payload }: ActionType<typeof resourceFavoriteActions.toggleResourceFavorite.success>,
): ResourcesState => {
  if (!state.favoritesOnly || payload.isFavorite) {
    return state;
  }

  return {
    ...state,
    resources: removeResource(state.resources, payload.resourceUuid),
    infiniteResources: Object.fromEntries(
      Object.entries(state.infiniteResources).map(([indexOfSet, resources]) => [
        indexOfSet,
        removeResource(resources, payload.resourceUuid),
      ]),
    ),
  };
};

const resourcesReducer = createReducer(initialState)
  .handleAction(actions.getResources.request, getResourcesRequestHandler)
  .handleAction(actions.getResources.success, getResourcesSuccessHandler)
  .handleAction(actions.getResources.failure, getResourcesFailureHandler)
  .handleAction(actions.resetResourcesState, resetResourceHandler)
  .handleAction(actions.getInfiniteResources.request, getInfiniteResourcesRequestHandler)
  .handleAction(actions.getInfiniteResources.success, getInfiniteResourcesSuccessHandler)
  .handleAction(actions.getInfiniteResources.failure, getInfiniteResourcesFailureHandler)
  .handleAction(actions.resetInfiniteResourcesState, resetInfiniteResourcesStateHandler)
  .handleAction(resourceFavoriteActions.toggleResourceFavorite.request, toggleResourceFavoriteRequestHandler)
  .handleAction(resourceFavoriteActions.toggleResourceFavorite.success, toggleResourceFavoriteSuccessHandler)
  .handleAction(resourceFavoriteActions.toggleResourceFavorite.failure, toggleResourceFavoriteFailureHandler);

export default resourcesReducer;
